"use client";

import React from "react";
import { Tooltip, Tag } from "@/ui";
import { colors, spacing, radius, textStyles } from "@/ui/tokens";
import {
  Refresh,
  Person,
  Business,
  Work,
  Dashboard,
  Analytics,
  Settings as SettingsIcon,
  Info
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";

export function ContextBar({ context }) {
  const getPageIcon = () => {
    switch (context.pageType) {
      case "candidate":
        return <Person style={{ fontSize: 16 }} />;
      case "company":
        return <Business style={{ fontSize: 16 }} />;
      case "job":
        return <Work style={{ fontSize: 16 }} />;
      case "dashboard":
        return <Dashboard style={{ fontSize: 16 }} />;
      case "reports":
        return <Analytics style={{ fontSize: 16 }} />;
      case "settings":
        return <SettingsIcon style={{ fontSize: 16 }} />;
      default:
        return <Info style={{ fontSize: 16 }} />;
    }
  };

  const getTagColor = () => {
    switch (context.pageType) {
      case "candidate":
        return "info";
      case "company":
        return "warning";
      case "job":
        return "success";
      default:
        return "neutral";
    }
  };

  const truncateId = (id) => {
    if (!id || id.length <= 8) return id;
    return `${id.slice(0, 8)}`;
  };

  const getContextTitle = () => {
    const baseTitle = context.pageType.charAt(0).toUpperCase() + context.pageType.slice(1);

    if (context.entityId) {
      const shortId = truncateId(context.entityId);
      switch (context.pageType) {
        case "candidate":
          return `Candidate ${shortId}`;
        case "company":
          return `Company ${shortId}`;
        case "job":
          return `Job ${shortId}`;
        case "float":
          return `Float ${shortId}`;
        default:
          return `${baseTitle} ${shortId}`;
      }
    }

    return baseTitle;
  };

  const handleRefreshContext = () => {
    // Context refresh handler - currently no-op
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      <div
        style={{
          height: 40,
          paddingLeft: spacing[4],
          paddingRight: spacing[4],
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          backgroundColor: colors.canvas.card,
          borderBottom: `1px solid ${colors.border.subtle}`,
        }}
      >
        {/* Context Info */}
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], flex: 1, minWidth: 0 }}>
          <Tag variant="filled" color={getTagColor()} size="sm">
            <span style={{ display: "inline-flex", alignItems: "center", gap: spacing[1] }}>
              {getPageIcon()}
              {getContextTitle()}
            </span>
          </Tag>

          {/* Additional context indicators */}
          <AnimatePresence>
            {context.availableActions && context.availableActions.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <Tooltip content={`${context.availableActions.length} actions available`}>
                  <Tag variant="filled" color="success" size="sm">
                    {context.availableActions.length} actions
                  </Tag>
                </Tooltip>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", alignItems: "center", gap: spacing[1] }}>
          <Tooltip content="Refresh context">
            <button
              onClick={handleRefreshContext}
              style={{
                width: 24,
                height: 24,
                border: "none",
                background: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: radius.sm,
                color: colors.text.muted,
                padding: 0,
                transition: "color 0.15s ease, background-color 0.15s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = colors.action.product;
                e.currentTarget.style.backgroundColor = colors.action.productLight;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = colors.text.muted;
                e.currentTarget.style.backgroundColor = "transparent";
              }}
            >
              <Refresh style={{ fontSize: 14 }} />
            </button>
          </Tooltip>
        </div>
      </div>
    </motion.div>
  );
}
